
import { defineComponent, inject, onMounted, PropType, ref, watch } from 'vue';
import { useGoogleMaps } from '@/services';
import { InjectionKeys } from '@/constants';
import { geoPointToLatLng, latLngToGeoPoint, truncateLatLng } from '@/utils';
import TextInputStyleProvider from '@/components/style-providers/TextInputStyleProvider.vue';
import Label from '@/components/form-controls/base/Label.vue';
import { Location } from '@/types/shared';
import { useStore } from '@/store';
import { Bid } from '@/types/app';

export default defineComponent({
  name: 'LocationInput',
  emits: ['update:modelValue'],
  components: {
    TextInputStyleProvider,
    Label
  },
  props: {
    modelValue: {
      type: Object as PropType<Location>
    },
    label: {
      type: String,
      default: 'Location'
    },
    highlightDiv: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean
    }
  },
  setup(props, { emit }) {
    const { useLatLngOnClick, useAutocomplete, useOriginMarker } = useGoogleMaps();
    const locationInput = ref<HTMLInputElement>();
    const autocomplete = ref<google.maps.places.Autocomplete>();
    const { setOriginMarker, removeOriginMarker } = useOriginMarker();
    const setDirty = inject(InjectionKeys.SetFormDirty);

    onMounted(() => {
      const [latLng] = useLatLngOnClick();

      if (locationInput.value && props.modelValue) {
        locationInput.value.value = truncateLatLng(geoPointToLatLng(props.modelValue.geoPoint))
          .toString()
          .replaceAll(/([()])/g, '');
      }

      watch(latLng, () => {
        if (locationInput.value && latLng.value) {
          setOriginMarker(latLng.value);
          const location: Location = {
            geoPoint: latLngToGeoPoint(latLng.value),
            address: ''
          };
          emit('update:modelValue', location);
          setDirty?.();
          locationInput.value.value =
            latLng.value &&
            truncateLatLng(latLng.value)
              .toString()
              .replaceAll(/([()])/g, '');
        }
      });

      autocomplete.value = locationInput.value && useAutocomplete(locationInput.value);
      autocomplete.value?.addListener;

      autocomplete.value?.addListener('place_changed', () => {
        const place = autocomplete.value?.getPlace();

        if (!place?.geometry && locationInput.value) {
          // Lat/lng regex matching.
          if (locationInput.value.value.match(/^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/)) {
            const [lat, lng] = locationInput.value.value.split(',');
            const latLngValue = new google.maps.LatLng(Number.parseFloat(lat), Number.parseFloat(lng));
            setOriginMarker(latLngValue, true);
            const location: Location = {
              geoPoint: latLngToGeoPoint(latLngValue),
              address: ''
            };
            emit('update:modelValue', location);
            setDirty?.();
            return;
          }

          locationInput.value.value = '';
          removeOriginMarker();

          return;
        }

        if (place && place.geometry && place.geometry.location && place.formatted_address) {
          setOriginMarker(place.geometry.location, true);
          const location: Location = {
            geoPoint: latLngToGeoPoint(place.geometry.location),
            address: place.formatted_address
          };

          const store = useStore();
          store.dispatch.bids.updateLocalBid({
            jobLocation: place.formatted_address
          } as Bid);

          emit('update:modelValue', location);
          setDirty?.();
        }
      });
    });

    return {
      locationInput
    };
  }
});
